import dayjs from "dayjs";
import { PROMO_KPIS_METRICS } from "../../utils/constants";
import formatarDuracao from "../../utils/formatarDuracao";

export const LIMIAR_DO_LIFT = 5;
export const JANELA_VIGENCIA = "vigencia";
export const JANELA_PRIMEIROS_DIAS = "primeiros_dias";
export const PERIODO_PADRAO = 7;
export const LIMITE_DE_DIAS_DA_SERIE = 800;
export const COR_DA_BASE = "#8C8C8C";
export const COR_DA_VIGENCIA = "#4A6FA5";
export const CORES_DAS_VIGENCIAS = ["#4A6FA5", "#E07B39", "#52A36B", "#9B59B6"];

const ehNumero = (valor) => typeof valor === "number" && Number.isFinite(valor);

const maiorData = (a, b) => (a > b ? a : b);

export const ontemLocal = () => dayjs().subtract(1, "day").format("YYYY-MM-DD");

export const periodoSemiaberto = (intervalo) => {
  const [inicio, fim] = Array.isArray(intervalo) ? intervalo : [];
  if (!inicio || !fim) return null;
  const mensal = String(inicio).length === 7;
  const primeiro = mensal ? dayjs(`${inicio}-01`) : dayjs(inicio);
  const ultimo = mensal ? dayjs(`${fim}-01`).endOf("month") : dayjs(fim);
  if (!primeiro.isValid() || !ultimo.isValid()) return null;
  return {
    start: primeiro.format("YYYY-MM-DD"),
    end: ultimo.add(1, "day").format("YYYY-MM-DD"),
  };
};

export const parametrosDaJanela = (escolha, eventoComVigencia = null) => {
  if (Number.isInteger(escolha) && escolha > 0) {
    return { period: escolha, janela: JANELA_PRIMEIROS_DIAS };
  }
  if (eventoComVigencia === false) {
    return { period: PERIODO_PADRAO, janela: JANELA_PRIMEIROS_DIAS };
  }
  return { period: PERIODO_PADRAO, janela: JANELA_VIGENCIA };
};

export const corDoLift = (variacao) => {
  if (variacao === Number.POSITIVE_INFINITY) return "green";
  if (!ehNumero(variacao)) return null;
  if (variacao > LIMIAR_DO_LIFT) return "green";
  if (variacao < -LIMIAR_DO_LIFT) return "red";
  return "yellow";
};

export const formatarPct = (valor, { comSinal = false } = {}) => {
  if (valor === Number.POSITIVE_INFINITY) return "Inf.";
  if (!ehNumero(valor)) return "—";
  const texto = valor.toLocaleString("pt-BR", {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  });
  return `${comSinal && valor > 0 ? "+" : ""}${texto}%`;
};

export const formatarMoeda = (valor) =>
  ehNumero(valor)
    ? valor.toLocaleString("pt-BR", { style: "currency", currency: "BRL" })
    : "—";

export const formatarData = (iso) =>
  iso ? dayjs(String(iso).slice(0, 10)).format("DD/MM/YYYY") : "—";

const TAGS_DE_STATUS = {
  SEM_ALTERACAO_DE_PRECO: { color: "gold", label: "Sem alteração de preço" },
  RUPTURA: { color: "red", label: "Ruptura" },
  SEM_VENDA: { color: "default", label: "Sem venda no período" },
  VENDEU_COM_DESCONTO: { color: "green", label: "Vendeu com desconto" },
};

export const STATUS_DO_SKU = Object.keys(TAGS_DE_STATUS);

export const tagDoStatus = (status) =>
  TAGS_DE_STATUS[status] || { color: "default", label: status || "—" };

const FONTES_DE_EXPOSICAO = {
  jasper: "Planograma (Jasper)",
  manual: "Vínculo manual",
  "jasper+manual": "Planograma e vínculo manual",
  evento: "Zonas do evento",
};

const MOTIVOS_SEM_EXPOSICAO = {
  sem_ean: "SKU sem EAN no cadastro e sem vínculo manual a uma categoria.",
  sem_vinculo:
    "SKU fora do planograma e sem vínculo manual a uma categoria com zona na vigência.",
  sem_periodo: "Nenhum dia da vigência foi analisado ainda.",
};

export const exposicaoDoSku = (sku) => {
  const exposicao = sku?.exposicao;
  if (exposicao) {
    const fonte = FONTES_DE_EXPOSICAO[exposicao.fonte] || exposicao.fonte;
    const zonas = (exposicao.zonas || []).join(", ");
    return {
      minutos: exposicao.minutos,
      rPorMin: exposicao.r_por_min,
      rotulo: null,
      dica: zonas ? `${fonte} · ${zonas}` : fonte,
    };
  }
  const motivo = MOTIVOS_SEM_EXPOSICAO[sku?.exposicao_motivo] || null;
  const colecao = sku?.exposicao_colecao;
  if (colecao) {
    return {
      minutos: colecao.minutos,
      rPorMin: colecao.r_por_min,
      rotulo: `categoria ${colecao.categoria || colecao.colecao || colecao.collection_legacy_id}`,
      dica: [motivo, "Mostra a atenção da categoria inteira, não a do SKU."]
        .filter(Boolean)
        .join(" "),
    };
  }
  return { minutos: null, rPorMin: null, rotulo: null, dica: motivo };
};

export const formatarUnidades = (valor) =>
  ehNumero(valor) ? `${valor.toLocaleString("pt-BR")} un.` : "—";

export const formatarMinutos = (valor) =>
  ehNumero(valor) ? formatarDuracao(valor) : "—";

export const deltaDoValor = (texto, variacao) =>
  texto === "—" || !ehNumero(variacao)
    ? null
    : `(${formatarPct(variacao, { comSinal: true })})`;

export const tipoDoDelta = (variacao) =>
  ({ green: "success", red: "danger" })[corDoLift(variacao)] || "secondary";

const numeroOuNulo = (valor) => (ehNumero(valor) ? valor : null);

export const linhaDoSku = (sku) => {
  const campanha = sku?.periodos?.campanha || null;
  const variacao = sku?.periodos?.variacao_pct || {};
  const doPeriodo = (chave, reserva) =>
    numeroOuNulo(campanha ? campanha[chave] : reserva);
  const deltaDoPeriodo = (chave) =>
    campanha ? numeroOuNulo(variacao[chave]) : null;

  const vendas = doPeriodo("unidades", sku?.unidades);
  const faturamento = doPeriodo("faturamento_vendas", sku?.faturamento_vendas);
  const margem = doPeriodo("margem", sku?.margem);
  const minutosDoSku = numeroOuNulo(campanha?.exposicao_min);
  const exposicao = exposicaoDoSku(sku);
  const atencao =
    minutosDoSku !== null
      ? {
          texto: formatarMinutos(minutosDoSku),
          variacao: deltaDoPeriodo("exposicao_min"),
          rotulo: null,
          dica: exposicao.dica,
        }
      : {
          texto: formatarMinutos(exposicao.minutos),
          variacao: null,
          rotulo: exposicao.rotulo,
          dica: exposicao.dica,
        };

  return {
    vendas: {
      texto: formatarUnidades(vendas),
      variacao: deltaDoPeriodo("unidades"),
    },
    faturamento: {
      texto: formatarMoeda(faturamento),
      variacao: deltaDoPeriodo("faturamento_vendas"),
    },
    margem: {
      texto: formatarMoeda(margem),
      variacao: deltaDoPeriodo("margem"),
      dica: ehNumero(sku?.delta_margem)
        ? `Variação em reais: ${formatarMoeda(sku.delta_margem)}`
        : null,
    },
    atencao,
  };
};

const TAGS_DE_SITUACAO = {
  FUTURA: { color: "purple", label: "Futura" },
  EM_CURSO: { color: "blue", label: "Em curso" },
  ENCERRADA: { color: "default", label: "Encerrada" },
  RETIRADA: { color: "volcano", label: "Retirada" },
};

export const tagDaSituacao = (situacao) => TAGS_DE_SITUACAO[situacao] || null;

export const OPCOES_DE_MECANICA = [
  { value: "preco_fixo", label: "Preço fixo" },
  { value: "leve_pague", label: "Leve X pague Y" },
  { value: "desconto_pct", label: "Desconto percentual" },
  { value: "combo", label: "Combo/kit" },
  { value: "escalonado", label: "Preço escalonado por quantidade" },
];

export const rotuloDaMecanica = (mecanica) =>
  OPCOES_DE_MECANICA.find((opcao) => opcao.value === mecanica)?.label || null;

export const rotuloDaFamilia = (familia) =>
  ({ vigencia: "Vigência", encarte: "Encarte" })[familia] || null;

const rotuloDosProdutos = (evento) => {
  const temSkus = ehNumero(evento.n_skus);
  if (ehNumero(evento.n_categorias) && evento.n_categorias > 0) {
    return temSkus ? `Categoria (${evento.n_skus} SKUs)` : "Categoria";
  }
  return temSkus ? `SKUs (${evento.n_skus})` : "SKUs";
};

export const rotuloDoEscopo = (evento) => {
  if (!evento) return null;
  if (evento.escopo === "produto") {
    return { color: "magenta", label: rotuloDosProdutos(evento) };
  }
  if (evento.escopo === "misto") {
    return { color: "purple", label: `Zonas + ${rotuloDosProdutos(evento)}` };
  }
  return {
    color: "cyan",
    label: evento.n_zonas > 1 ? `Zonas (${evento.n_zonas})` : "Zona",
  };
};

export const rotuloDaOrigem = (origem) =>
  origem === "erp"
    ? { color: "geekblue", label: "ERP" }
    : { color: "default", label: "Manual" };

export const analisePorProduto = (evento) =>
  evento?.escopo === "produto" || evento?.escopo === "misto";

export const chaveDoGrupo = (evento) =>
  evento.group_id
    ? `grupo:${evento.group_id}`
    : `manual:${evento.titulo}||${evento.datetime}||${evento.end_datetime || ""}||${evento.comentario || ""}`;

export const agruparEventos = (eventos) => {
  const grupos = new Map();
  (eventos || []).forEach((evento) => {
    const agrupavel =
      evento.tipo === "EVENTO" &&
      evento.escopo === "zona" &&
      evento.origem !== "erp";
    const chave = agrupavel
      ? chaveDoGrupo(evento)
      : `evento:${evento.event_id}`;
    if (!grupos.has(chave)) grupos.set(chave, []);
    grupos.get(chave).push(evento);
  });

  return Array.from(grupos.entries()).map(([chave, filhos]) => {
    const [primeiro] = filhos;
    if (filhos.length === 1) {
      return {
        ...primeiro,
        key: `evento:${primeiro.event_id}`,
        isGroup: false,
      };
    }
    return {
      ...primeiro,
      key: chave,
      isGroup: true,
      zona_nome: filhos
        .map((filho) => filho.zona_nome)
        .filter(Boolean)
        .join(", "),
      n_zonas: filhos.length,
      event_ids: filhos.map((filho) => filho.event_id),
      _children: filhos.map((filho) => ({
        ...filho,
        key: `evento:${filho.event_id}`,
        isGroup: false,
        _isChild: true,
      })),
    };
  });
};

export const filtrarPorZonas = (eventos, zonas) => {
  if (!Array.isArray(zonas) || zonas.length === 0 || zonas.includes("all")) {
    return eventos || [];
  }
  const selecionadas = new Set(zonas.map(String));
  return (eventos || []).filter(
    (evento) =>
      evento.escopo !== "zona" ||
      selecionadas.has(String(evento.zona_id)) ||
      selecionadas.has(String(evento.zona_nome))
  );
};

const variacaoDoEixo = (eixo) => {
  if (!eixo) return null;
  if (ehNumero(eixo.variacao_pct)) return eixo.variacao_pct;
  if (
    eixo.total_antes === 0 &&
    eixo.total_depois > 0 &&
    eixo.media_diaria_antes === 0
  ) {
    return Number.POSITIVE_INFINITY;
  }
  return null;
};

export const resumoDoImpacto = (impacto) => {
  if (!impacto) return null;
  const eixoDoFaturamento =
    impacto.escopo === "zona"
      ? impacto.faturamento
      : impacto.campanha?.lift_faturamento;
  const atencao =
    impacto.atencao && "variacao_pct" in impacto.atencao
      ? impacto.atencao
      : null;
  return {
    faturamento_pct: variacaoDoEixo(eixoDoFaturamento),
    delta_total: eixoDoFaturamento?.delta_total ?? null,
    atencao_pct: variacaoDoEixo(atencao),
    atencao_motivo: atencao ? null : (impacto.atencao?.motivo ?? null),
  };
};

export const normalizarTexto = (valor) =>
  String(valor || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .trim();

const listaDeCategorias = (valor) =>
  String(valor || "")
    .split(",")
    .map((item) => normalizarTexto(item))
    .filter(Boolean);

export const filtrarTrocasPorCategoria = (trocas, rotulos) => {
  const procurados = (rotulos || []).map(normalizarTexto).filter(Boolean);
  if (procurados.length === 0) return trocas || [];
  return (trocas || []).filter((troca) => {
    const categorias = [
      ...listaDeCategorias(troca.colecao_anterior),
      ...listaDeCategorias(troca.colecao_nova),
    ];
    return procurados.some((rotulo) => categorias.includes(rotulo));
  });
};

export const linhasDeTrocas = (trocas) =>
  [...(trocas || [])]
    .sort((a, b) => String(b.datetime).localeCompare(String(a.datetime)))
    .map((troca) => ({
      ...troca,
      key: `troca:${troca.collection_change_id}`,
      isGroup: false,
    }));

const somarDoEixo = (impactos, eixo, chave) =>
  impactos.reduce(
    (soma, impacto) =>
      soma + (ehNumero(impacto[eixo]?.[chave]) ? impacto[eixo][chave] : 0),
    0
  );

const variacaoEntre = (antes, depois) => {
  if (antes > 0) return (depois / antes - 1) * 100;
  if (antes === 0 && depois > 0) return Number.POSITIVE_INFINITY;
  return null;
};

export const resumoDeVariosImpactos = (impactos) => {
  const validos = (impactos || []).filter(Boolean);
  if (validos.length === 0) return null;
  if (validos.length === 1) return resumoDoImpacto(validos[0]);
  const comAtencao = validos.filter(
    (impacto) => impacto.atencao && "variacao_pct" in impacto.atencao
  );
  return {
    faturamento_pct: variacaoEntre(
      somarDoEixo(validos, "faturamento", "total_antes"),
      somarDoEixo(validos, "faturamento", "total_depois")
    ),
    delta_total: somarDoEixo(validos, "faturamento", "delta_total"),
    atencao_pct: comAtencao.length
      ? variacaoEntre(
          somarDoEixo(comAtencao, "atencao", "total_antes"),
          somarDoEixo(comAtencao, "atencao", "total_depois")
        )
      : null,
    atencao_motivo: null,
  };
};

const limitarAOntem = (data, ontem) => {
  if (!data) return ontem;
  return ontem && data > ontem ? ontem : data;
};

export const janelaDoGrafico = (janela, dias, ontem) => {
  const inicioDoEvento = janela?.evento?.inicio;
  if (!inicioDoEvento) return null;
  const inicio = janela.baseline?.inicio || inicioDoEvento;
  const datas = [
    janela.evento.fim || ontem,
    ...(dias || []).map((dia) => dia.date),
  ].filter(Boolean);
  let fim = datas.reduce(maiorData, inicio);
  if (ontem && fim > ontem && inicioDoEvento <= ontem) fim = ontem;
  return { inicio, fim };
};

export const faixasDeVigencia = (janela, ontem) => {
  const inicio = janela?.evento?.inicio;
  if (!inicio) return [];
  const faixas = [];
  if (janela.baseline?.inicio && janela.baseline?.fim) {
    faixas.push({
      inicio: janela.baseline.inicio,
      fim: janela.baseline.fim,
      rotulo: "Base",
      cor: COR_DA_BASE,
    });
  }
  faixas.push({
    inicio,
    fim: maiorData(inicio, limitarAOntem(janela.evento.fim, ontem) || inicio),
    rotulo: "Vigência",
    cor: COR_DA_VIGENCIA,
  });
  return faixas;
};

export const faixasDasVigencias = (vigencias, ontem) =>
  (vigencias || []).map((vigencia, indice) => ({
    inicio: vigencia.data_inicio,
    fim: maiorData(
      vigencia.data_inicio,
      limitarAOntem(vigencia.data_fim, ontem) || vigencia.data_inicio
    ),
    rotulo: vigencia.title,
    cor: CORES_DAS_VIGENCIAS[indice % CORES_DAS_VIGENCIAS.length],
  }));

export const dataParaTimestamp = (iso) => {
  const [ano, mes, dia] = String(iso).slice(0, 10).split("-").map(Number);
  return new Date(ano, mes - 1, dia).getTime();
};

export const densificarSerie = (dias, inicio, fim) => {
  if (!inicio || !fim || fim < inicio) return [];
  const porData = new Map((dias || []).map((dia) => [dia.date, dia]));
  const resultado = [];
  const ultimo = dayjs(fim);
  let cursor = dayjs(inicio);
  while (
    !cursor.isAfter(ultimo, "day") &&
    resultado.length < LIMITE_DE_DIAS_DA_SERIE
  ) {
    const data = cursor.format("YYYY-MM-DD");
    resultado.push(porData.get(data) || { date: data, semDado: true });
    cursor = cursor.add(1, "day");
  }
  return resultado;
};

export const serieParaApex = (dias, campo, preenchimento = 0) =>
  (dias || []).map((dia) => ({
    x: dataParaTimestamp(dia.date),
    y: ehNumero(dia[campo]) ? dia[campo] : dia.semDado ? preenchimento : null,
  }));

export const kpisDaCampanha = (campanha, entradas) => {
  const chaves = PROMO_KPIS_METRICS.flatMap((kpi) =>
    (kpi.metrics || [kpi]).map((metrica) => metrica.key)
  );
  if (!campanha) return { dados: {}, indisponiveis: chaves };
  const dados = {
    ...campanha,
    lift_unidades_pct: campanha.lift_unidades?.variacao_pct ?? null,
    lift_faturamento_pct: campanha.lift_faturamento?.variacao_pct ?? null,
    variacao_unidades: campanha.periodos?.variacao_pct?.unidades ?? null,
    variacao_faturamento_vendas:
      campanha.periodos?.variacao_pct?.faturamento_vendas ?? null,
    variacao_margem: campanha.periodos?.variacao_pct?.margem ?? null,
    variacao_margem_pct:
      campanha.periodos?.antes?.margem_pct != null &&
      campanha.periodos?.campanha?.margem_pct != null
        ? campanha.periodos.campanha.margem_pct -
          campanha.periodos.antes.margem_pct
        : null,
    entradas: entradas?.total_depois ?? null,
    variacao_entradas: entradas?.variacao_pct ?? null,
  };
  const semEstoque = !campanha.estoque_ref;
  return {
    dados,
    indisponiveis: chaves.filter(
      (chave) =>
        !ehNumero(dados[chave]) || (semEstoque && chave === "esgotados")
    ),
  };
};

export const textoDosRetratos = (estoqueRef, ultimoRetratoErp) => {
  const estoque = estoqueRef
    ? `Estoque de ${formatarData(estoqueRef)}`
    : "Loja sem estoque coletado do ERP";
  return ultimoRetratoErp
    ? `${estoque} · último retrato de promoções do ERP em ${formatarData(ultimoRetratoErp)}`
    : estoque;
};

const faixaDeDias = (dias) => {
  if (!Array.isArray(dias) || dias.length === 0) return null;
  const inicio = dias[0];
  const fim = dias[dias.length - 1];
  const corridos = dayjs(fim).diff(dayjs(inicio), "day") + 1;
  return {
    inicio,
    fim,
    periodo: `${formatarData(inicio)} a ${formatarData(fim)}`,
    abertos: dias.length,
    corridos,
    fechados: corridos - dias.length,
  };
};

export const textoDosDias = (faixa) => {
  if (!faixa) return "";
  const rotulo = faixa.abertos === 1 ? "dia aberto" : "dias abertos";
  if (faixa.fechados <= 0) return `${faixa.abertos} ${rotulo}`;
  return `${faixa.abertos} ${rotulo} em ${faixa.corridos} de calendário`;
};

export const resumoDasJanelas = (diasAntes, diasDepois) => {
  const evento = faixaDeDias(diasDepois);
  const comparativo = faixaDeDias(diasAntes);
  if (!evento || !comparativo) return null;
  return {
    evento,
    comparativo,
    tamanhosDiferentes: evento.abertos !== comparativo.abertos,
  };
};

export const METRICAS_DO_COMPARATIVO = [
  { key: "preco_medio", titulo: "Preço médio", formato: "moeda" },
  { key: "custo_unitario", titulo: "Custo un.", formato: "moeda" },
  { key: "margem_unitaria", titulo: "Margem un.", formato: "moeda" },
  { key: "exposicao_min", titulo: "Atenção", formato: "minutos" },
  { key: "unidades", titulo: "Vendas (un)", formato: "quantidade" },
  { key: "faturamento_vendas", titulo: "Faturamento", formato: "moeda" },
  { key: "margem", titulo: "Margem", formato: "moeda" },
];

const valoresDoPeriodo = (periodo) =>
  Object.fromEntries(
    METRICAS_DO_COMPARATIVO.map(({ key }) => [
      key,
      ehNumero(periodo?.[key]) ? periodo[key] : null,
    ])
  );

const linhaDoPeriodo = (key, rotulo, periodo) => ({
  key,
  rotulo,
  periodo: periodo
    ? `${formatarData(periodo.inicio)} a ${formatarData(periodo.fim)}`
    : "—",
  dias: ehNumero(periodo?.dias) ? periodo.dias : null,
  ...valoresDoPeriodo(periodo),
});

export const comparativoDoSku = (sku) => {
  const periodos = sku?.periodos;
  if (!periodos?.campanha) return null;
  const antes = periodos.antes || null;
  return {
    linhas: [
      linhaDoPeriodo("antes", "Período anterior", antes),
      linhaDoPeriodo("campanha", "Período campanha", periodos.campanha),
      {
        key: "variacao",
        rotulo: "Resultado (Δ)",
        periodo: null,
        dias: null,
        ...valoresDoPeriodo(periodos.variacao_pct),
      },
    ],
    semBase: !antes,
    tamanhosDiferentes: Boolean(antes) && antes.dias !== periodos.campanha.dias,
  };
};

const MENSAGENS_POR_CODIGO = {
  EVENTO_SEM_ESCOPO_DE_PRODUTO:
    "Este evento é por zona: não tem SKUs para listar.",
  unsupported_scope:
    "Este evento é por produto: a análise por zona não se aplica.",
};

export const mensagemDeErro = (
  erro,
  padrao = "Não foi possível carregar os dados."
) => {
  const resposta = erro?.response;
  const dados = resposta?.data;
  if (dados && typeof dados === "object") {
    const codigo = dados.error || dados.status;
    if (MENSAGENS_POR_CODIGO[codigo]) return MENSAGENS_POR_CODIGO[codigo];
    if (typeof dados.error === "string") return dados.error;
    if (typeof dados.detail === "string") return dados.detail;
    for (const valor of Object.values(dados)) {
      if (typeof valor === "string") return valor;
      if (Array.isArray(valor) && typeof valor[0] === "string") return valor[0];
    }
  }
  if (resposta?.status === 404) return "Evento não encontrado ou sem acesso.";
  return padrao;
};

export const podeEditarEvento = (user, evento) =>
  Boolean(
    (user?.is_superuser || user?.is_client_admin) &&
      evento &&
      evento.tipo === "EVENTO" &&
      !evento.isGroup &&
      evento.event_id !== null &&
      evento.event_id !== undefined
  );

// ─── Agrupamento por categoria (SKUs da campanha) ────────────────────────────

export const TAMANHO_DO_LOTE_DE_SKUS = 20;

// Vendas, faturamento e margem de uma categoria ou do total geral. Os demais
// indicadores (atenção, estoque, status...) só existem por SKU.
export const linhaDoAgregado = (agregado) => {
  if (!agregado || !agregado.skus) return null;
  const semCusto = agregado.skus_sem_custo || 0;
  const comCusto = (agregado.skus || 0) - semCusto;
  const margemPct = agregado.margem_pct?.campanha;
  // "57,4% de margem" = margem ÷ faturamento dos SKUs que entraram na conta.
  const rotuloDaMargem = [
    ehNumero(margemPct) ? `${formatarPct(margemPct)} de margem` : null,
    semCusto > 0 ? "parcial" : null,
  ]
    .filter(Boolean)
    .join(" · ");
  const explicacaoDaMargem =
    "Margem em % do faturamento (margem ÷ faturamento), só dos SKUs com custo cadastrado.";
  const deEixo = (eixo, formatar) => ({
    texto: formatar(numeroOuNulo(eixo?.campanha)),
    variacao: numeroOuNulo(eixo?.variacao_pct),
  });
  return {
    vendas: deEixo(agregado.vendas, formatarUnidades),
    faturamento: deEixo(agregado.faturamento, formatarMoeda),
    margem: {
      ...deEixo(agregado.margem, formatarMoeda),
      rotulo: rotuloDaMargem || null,
      dica:
        semCusto > 0
          ? `${explicacaoDaMargem} Calculada com ${comCusto} de ${agregado.skus} SKUs: ${semCusto} sem custo cadastrado ficaram de fora.`
          : explicacaoDaMargem,
    },
  };
};

const EIXO_DA_COLUNA = {
  vendas: "vendas",
  faturamento: "faturamento",
  margem: "margem",
};

// Categorias seguem a mesma coluna/direção dos SKUs. Margem ordena pela
// variação em R$ (como o SKU); vendas e faturamento, pelo valor da campanha.
export const ordenarCategorias = (categorias, { coluna, direcao } = {}) => {
  // Antes da resposta chegar a lista ainda não existe.
  if (!categorias) return [];
  const eixo = EIXO_DA_COLUNA[coluna];
  if (!eixo || !direcao) return categorias;
  const campo = eixo === "margem" ? "delta_total" : "campanha";
  const sinal = direcao === "ascend" ? 1 : -1;
  const valor = (categoria) => numeroOuNulo(categoria[eixo]?.[campo]);
  return [...categorias].sort((a, b) => {
    const va = valor(a);
    const vb = valor(b);
    if (va === null && vb === null) return a.name.localeCompare(b.name);
    if (va === null) return 1;
    if (vb === null) return -1;
    return sinal * (va - vb) || a.name.localeCompare(b.name, "pt-BR");
  });
};

// Linhas da tabela agrupada: categoria, seus SKUs (se abertas) e, quando há
// mais SKUs que o carregado, uma linha "mostrar mais".
export const linhasAgrupadas = (categorias, abertas, skusPorCategoria) =>
  (categorias || []).flatMap((categoria) => {
    const linha = {
      tipo: "categoria",
      rowKey: `cat:${categoria.key}`,
      categoria,
    };
    if (!abertas?.[categoria.key]) return [linha];
    const carregado = skusPorCategoria?.[categoria.key];
    const skus = carregado?.skus || [];
    const filhos = skus.map((sku) => ({
      ...sku,
      tipo: "sku",
      rowKey: `sku:${categoria.key}:${sku.produto}`,
    }));
    if (carregado?.carregando && skus.length === 0) {
      filhos.push({ tipo: "carregando", rowKey: `load:${categoria.key}` });
    } else if (skus.length < (carregado?.total ?? 0)) {
      filhos.push({
        tipo: "mais",
        rowKey: `mais:${categoria.key}`,
        categoriaKey: categoria.key,
        carregados: skus.length,
        total: carregado.total,
      });
    }
    return [linha, ...filhos];
  });

// O gráfico do SKU cobre só a janela comparada (do início do período anterior
// ao fim do período da campanha). Vigências do mesmo SKU que começam antes
// (promoção anterior que encosta no período anterior) ou terminam depois
// ("primeiros 30 dias" de uma promoção de 60) são recortadas, em vez de
// esticar o eixo. Sem janela conhecida, não recorta nada.
export const faixasNaJanela = (faixas, janela) => {
  if (!janela) return faixas;
  return (faixas || [])
    .filter((faixa) => faixa.fim >= janela.inicio && faixa.inicio <= janela.fim)
    .map((faixa) => ({
      ...faixa,
      inicio: faixa.inicio < janela.inicio ? janela.inicio : faixa.inicio,
      fim: faixa.fim > janela.fim ? janela.fim : faixa.fim,
    }));
};

// Recorte do gráfico do SKU: o mesmo eixo do gráfico do evento (do início do
// período anterior ao fim do período comparado, nunca depois de ontem) e as
// vigências do SKU recortadas nele. Nulo quando não há o que desenhar.
export const recorteDoGraficoDoSku = (resposta, serieDoEvento, ontem) => {
  const janela = janelaDoGrafico(
    serieDoEvento?.janela,
    serieDoEvento?.dias,
    ontem
  );
  const faixas = faixasNaJanela(
    faixasDasVigencias(resposta?.vigencias, ontem),
    janela
  );
  const datas = datasNaJanela(
    [
      ...(resposta?.dias || []).map((dia) => dia.date),
      ...faixas.flatMap((faixa) => [faixa.inicio, faixa.fim]),
    ].filter(Boolean),
    janela
  ).sort();
  if (datas.length === 0) return null;
  return {
    faixas,
    inicio: janela ? janela.inicio : datas[0],
    fim: janela ? janela.fim : datas[datas.length - 1],
  };
};

export const datasNaJanela = (datas, janela) =>
  janela
    ? (datas || []).filter(
        (data) => data >= janela.inicio && data <= janela.fim
      )
    : datas;

// ─── Média móvel dos gráficos diários ────────────────────────────────────────

// Janela em dias escolhida no controle dos gráficos; 1 é o diário.
export const JANELA_PADRAO_DA_MEDIA_MOVEL = 3;
export const JANELA_MAXIMA_DA_MEDIA_MOVEL = 14;

// Dia sem registro na série densificada conta como zero (venda zero); valor
// nulo (ex.: margem sem custo) fica fora da média em vez de virar zero.
const valorDoDia = (dia, campo) => {
  if (ehNumero(dia[campo])) return dia[campo];
  return dia.semDado ? 0 : null;
};

export const valoresDiarios = (dias, campo) =>
  (dias || []).map((dia) => valorDoDia(dia, campo));

// Média dos últimos `janela` dias, incluindo o próprio (nunca olha para frente,
// para a linha não subir antes de a promoção começar). Nos primeiros dias usa
// a janela parcial.
export const mediaMovelDaSerie = (dias, campo, janela) => {
  const valores = valoresDiarios(dias, campo);
  return valores.map((_, indice) => {
    const conhecidos = valores
      .slice(Math.max(0, indice - janela + 1), indice + 1)
      .filter((valor) => valor !== null);
    if (conhecidos.length === 0) return null;
    return (
      conhecidos.reduce((soma, valor) => soma + valor, 0) / conhecidos.length
    );
  });
};

export const serieMediaMovelParaApex = (dias, campo, janela) => {
  const medias = mediaMovelDaSerie(dias, campo, janela);
  return (dias || []).map((dia, indice) => ({
    x: dataParaTimestamp(dia.date),
    y: medias[indice],
  }));
};
